import { Injectable, InjectionToken, PLATFORM_ID, Pipe, PipeTransform, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DatePreferences, DisplayDateValue, DisplayDateVariant, formatDisplayDate, datePickerPattern } from './date-time';
export const DATE_PROFILE_URL = new InjectionToken<string>('DATE_PROFILE_URL');
/** Personal display settings for portals which span multiple organizations. */
@Injectable({ providedIn: 'root' })
export class ProfileDateService {
  private readonly preferences = signal<DatePreferences>({});
  private updated = false;
  constructor() {
    const url = inject(DATE_PROFILE_URL, { optional: true });
    const http = inject(HttpClient);
    if (url && isPlatformBrowser(inject(PLATFORM_ID))) {
      http.get<{timeZone?: string; locale?: string; preferences?: DatePreferences}>(url, {withCredentials: true})
        .subscribe({ next: profile => { if (!this.updated) this.applyProfile(profile); }, error: () => {} });
    }
  }
  /** Apply a freshly saved profile without reloading the portal or accepting an older in-flight response. */
  applyProfile(profile: { timeZone?: string; locale?: string; preferences?: DatePreferences }): void {
    this.updated = true;
    this.preferences.set({ ...profile.preferences, timeZone: profile.timeZone, locale: profile.locale });
  }
  /** The person's profile time zone, if they set one (null: the browser's). */
  get timeZone(): string | null { return this.preferences().timeZone || null; }
  get datePickerFormat(): string { return datePickerPattern(this.preferences().dateFormat); }
  get hourFormat(): string {
    const value = this.preferences().timeFormat || '';
    return value.includes('24') || value.includes('H') ? '24' : '12';
  }
  /** A time zone, when given, overrides the person's (e.g. a visit shown in its workspace's zone). */
  format(value: DisplayDateValue, variant: DisplayDateVariant = 'date', timeZone?: string | null): string {
    return formatDisplayDate(value, timeZone ? { ...this.preferences(), timeZone } : this.preferences(), variant);
  }
}
@Pipe({ name: 'profileDate', standalone: true, pure: false })
export class ProfileDatePipe implements PipeTransform {
  private readonly formatter = inject(ProfileDateService);
  transform(value: DisplayDateValue, format = 'date'): string {
    return this.formatter.format(value, ['short', 'medium', 'long', 'full'].includes(format) || /[hH]/.test(format) ? 'dateTime' : 'date');
  }
}
